import type { Relationship } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import { DiagnosticBag, type AnalysisDiagnostic } from './diagnostics.js';
import type { PlannedJoin } from './query-plan.js';

export interface JoinPlan {
  readonly baseTableId: string | null;
  /** Tabla base seguida de las tablas en el orden en que se incorporan. */
  readonly tableIds: readonly string[];
  readonly joins: readonly PlannedJoin[];
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

export interface PlanJoinsOptions {
  /** Si hay medidas, los joins uno a muchos se advierten (multiplican filas antes de agregar). */
  readonly hasMeasures?: boolean;
}

interface Edge {
  readonly relationship: Relationship;
  readonly fromTableId: string;
  readonly toTableId: string;
}

interface Step {
  readonly edge: Edge;
  /** Tabla desde la que se recorre el paso. */
  readonly from: string;
  readonly direction: 'forward' | 'reverse';
}

const FLIPPED: Readonly<Record<Relationship['cardinality'], Relationship['cardinality']>> = {
  'one-to-one': 'one-to-one',
  'one-to-many': 'many-to-one',
  'many-to-one': 'one-to-many',
  'many-to-many': 'many-to-many',
};

/**
 * Planifica los joins logicos necesarios para conectar las tablas requeridas con la
 * tabla base usando solo relaciones activas. Toma caminos minimos (BFS), no anade joins
 * innecesarios y reporta caminos ambiguos o inexistentes. No ejecuta joins.
 *
 * @example
 * const joins = planJoins(['orders', 'customers'], 'orders', index, { hasMeasures: true });
 * joins.joins[0]?.relationshipId; // 'orders_customers'
 */
export function planJoins(
  requiredTableIds: readonly string[],
  baseTableId: string | null,
  index: SemanticModelIndex,
  options: PlanJoinsOptions = {},
): JoinPlan {
  const bag = new DiagnosticBag();
  if (baseTableId === null) {
    return { baseTableId, tableIds: [], joins: [], diagnostics: bag.items };
  }
  if (!index.getTable(baseTableId)) {
    bag.error('missing-reference', `Tabla inexistente "${baseTableId}".`, {
      objectId: baseTableId,
    });
    return { baseTableId, tableIds: [], joins: [], diagnostics: bag.items };
  }

  const adjacency = buildAdjacency(index);
  const { distance, pathCount, parents } = shortestPaths(baseTableId, adjacency);

  const joined = new Set<string>([baseTableId]);
  const joins: PlannedJoin[] = [];
  for (const tableId of new Set(requiredTableIds)) {
    if (joined.has(tableId)) continue;
    if (!distance.has(tableId)) {
      const inactive = index.model.relationships.some(
        (relationship) =>
          !relationship.active &&
          [relationship.fromColumnId, relationship.toColumnId].some(
            (columnId) => index.getColumn(columnId)?.table.id === tableId,
          ),
      );
      bag.error(
        'missing-relationship',
        `No hay relaciones activas que conecten "${baseTableId}" con "${tableId}"${
          inactive ? ' (existen relaciones inactivas que podrian activarse)' : ''
        }.`,
        { objectId: tableId },
      );
      continue;
    }
    if ((pathCount.get(tableId) ?? 0) > 1) {
      const candidates = [...collectPathRelationships(tableId, baseTableId, parents)].sort();
      bag.error(
        'ambiguous-relationship',
        `Hay varios caminos igual de cortos entre "${baseTableId}" y "${tableId}" (relaciones: ${candidates.join(', ')}). Desactive las relaciones sobrantes.`,
        { objectId: tableId },
      );
      continue;
    }
    for (const step of pathTo(tableId, baseTableId, parents)) {
      const join = toJoin(step);
      if (joined.has(join.toTableId)) continue;
      joined.add(join.toTableId);
      joins.push(join);
      if (join.fanOut && options.hasMeasures === true) {
        bag.warning(
          'fan-out',
          `El join "${join.relationshipId}" (${join.cardinality}) puede duplicar filas antes de agregar.`,
          { objectId: join.relationshipId },
        );
      }
    }
  }

  return {
    baseTableId,
    tableIds: [baseTableId, ...joins.map((join) => join.toTableId)],
    joins,
    diagnostics: bag.items,
  };
}

function buildAdjacency(index: SemanticModelIndex): Map<string, Step[]> {
  const adjacency = new Map<string, Step[]>();
  const push = (tableId: string, step: Step): void => {
    const steps = adjacency.get(tableId);
    if (steps) steps.push(step);
    else adjacency.set(tableId, [step]);
  };
  for (const relationship of index.model.relationships) {
    if (!relationship.active) continue;
    const from = index.getColumn(relationship.fromColumnId);
    const to = index.getColumn(relationship.toColumnId);
    if (!from || !to || from.table.id === to.table.id) continue;
    const edge: Edge = { relationship, fromTableId: from.table.id, toTableId: to.table.id };
    push(edge.fromTableId, { edge, from: edge.fromTableId, direction: 'forward' });
    push(edge.toTableId, { edge, from: edge.toTableId, direction: 'reverse' });
  }
  return adjacency;
}

/** BFS con conteo de caminos minimos (acotado a 2: basta para detectar ambiguedad). */
function shortestPaths(
  baseTableId: string,
  adjacency: ReadonlyMap<string, readonly Step[]>,
): {
  distance: Map<string, number>;
  pathCount: Map<string, number>;
  parents: Map<string, Step[]>;
} {
  const distance = new Map<string, number>([[baseTableId, 0]]);
  const pathCount = new Map<string, number>([[baseTableId, 1]]);
  const parents = new Map<string, Step[]>();
  const queue = [baseTableId];
  // El iterador de arrays recorre tambien los elementos anadidos durante el bucle (cola BFS).
  for (const current of queue) {
    const currentDistance = distance.get(current) ?? 0;
    const currentCount = pathCount.get(current) ?? 0;
    for (const step of adjacency.get(current) ?? []) {
      const next = neighborOf(step);
      const known = distance.get(next);
      if (known === undefined) {
        distance.set(next, currentDistance + 1);
        pathCount.set(next, currentCount);
        parents.set(next, [step]);
        queue.push(next);
      } else if (known === currentDistance + 1) {
        pathCount.set(next, Math.min(2, (pathCount.get(next) ?? 0) + currentCount));
        parents.get(next)?.push(step);
      }
    }
  }
  return { distance, pathCount, parents };
}

const neighborOf = (step: Step): string =>
  step.direction === 'forward' ? step.edge.toTableId : step.edge.fromTableId;

function pathTo(
  tableId: string,
  baseTableId: string,
  parents: ReadonlyMap<string, readonly Step[]>,
): Step[] {
  const path: Step[] = [];
  let current = tableId;
  while (current !== baseTableId) {
    const step = parents.get(current)?.[0];
    if (!step) break;
    path.unshift(step);
    current = step.from;
  }
  return path;
}

function collectPathRelationships(
  tableId: string,
  baseTableId: string,
  parents: ReadonlyMap<string, readonly Step[]>,
): Set<string> {
  const ids = new Set<string>();
  const visited = new Set<string>();
  const visit = (current: string): void => {
    if (current === baseTableId || visited.has(current)) return;
    visited.add(current);
    for (const step of parents.get(current) ?? []) {
      ids.add(step.edge.relationship.id);
      visit(step.from);
    }
  };
  visit(tableId);
  return ids;
}

function toJoin(step: Step): PlannedJoin {
  const { relationship } = step.edge;
  const forward = step.direction === 'forward';
  const cardinality = forward ? relationship.cardinality : FLIPPED[relationship.cardinality];
  return {
    relationshipId: relationship.id,
    fromTableId: step.from,
    toTableId: neighborOf(step),
    fromColumnId: forward ? relationship.fromColumnId : relationship.toColumnId,
    toColumnId: forward ? relationship.toColumnId : relationship.fromColumnId,
    cardinality,
    direction: step.direction,
    fanOut: cardinality === 'one-to-many' || cardinality === 'many-to-many',
  };
}
